import apiV2Service from '../../api/api-v2.service';
import {ApiOptions} from '../../api/apiTypes';
import {BrandAssetFromUrlRequest, CreateBrandAssetRequest} from './brandAssets.types';
import {Asset} from '../Assets.types';
import {AssetType} from '../Assets.enums';

const BASE_URL = 'Clients/BrandAssets';

/**
 * List brand assets for the given client and asset type
 * @param assetType - Type of assets to list
 * @param options - API options
 * @returns Promise<BrandAsset[]> - Array of brand assets
 */
function getBrandAssets(
  assetType: AssetType,
  options?: ApiOptions,
): Promise<Asset[]> {
  const endpoint =
    assetType === AssetType.Image ? `${BASE_URL}/Images` : `${BASE_URL}/Videos`;

  return apiV2Service
    .executeGetRequest<Asset[]>(endpoint, {}, options)
    .then((response) => response.data);
}

/**
 * Creates a new brand asset from uploaded file
 * @param request - Brand asset creation request
 * @param options - API options
 * @returns Promise<string> - Created brand asset ID
 */
function createBrandAsset(request: CreateBrandAssetRequest, options?: ApiOptions): Promise<string> {
  const payload = {
    assetType: request.assetType,
    name: request.name,
    purpose: request.purpose,
    usageGuides: request.usageGuides,
  };

  return apiV2Service
    .uploadFile<string>({
      endpoint: BASE_URL,
      params: {},
      file: request.file,
      formDataKey: 'file',
      postData: payload,
      options,
      method: 'put',
    })
    .then((response) => response.data);
}

/**
 * Creates a new brand asset from URL
 * @param request - Brand asset from URL request
 * @param options - API options
 * @returns Promise<string> - Created brand asset ID
 */
function createBrandAssetFromUrl(
  request: BrandAssetFromUrlRequest,
  options?: ApiOptions,
): Promise<string> {
  return apiV2Service
    .executePutRequest<string>(
      `${BASE_URL}/FromUrl`,
      {},
      request as unknown as Record<string, unknown>,
      options,
    )
    .then((response) => response.data);
}

/**
 * Deletes a brand asset by ID
 * @param assetId - ID of the asset to delete
 * @param assetType - Type of the asset to delete
 * @param options - API options
 * @returns Promise<void>
 */
function deleteBrandAsset(
  assetId: string,
  assetType: AssetType,
  options?: ApiOptions,
): Promise<void> {
  const endpoint =
    assetType === AssetType.Image
      ? `${BASE_URL}/Images/${assetId}`
      : `${BASE_URL}/Videos/${assetId}`;

  return apiV2Service.executeDeleteRequest(endpoint, {}, options).then(() => {});
}

const brandAssetsApi = {
  getBrandAssets,
  createBrandAsset,
  createBrandAssetFromUrl,
  deleteBrandAsset,
};

export default brandAssetsApi;
